import { Grid, UI } from 'common';
import Utils from 'common/src/code/utils';

import Session from 'common/src/code/utils/Session';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import LabelStatusCellRenderer from './LabelStatusCellRenderer.component';

const { Section, SectionBody, Spinner, WrappedButton } = UI;
const { GridPanel } = Grid;
const {
	Logger: { logToServer },
} = Utils;

const formatDate = date => {
	const month = date.getMonth() + 1;
	const day = date.getDate();
	const year = date.getFullYear();
	return `${month}/${day}/${year}`;
};

class RefundEligibilityReport extends Component {
	constructor(props) {
		super(props);

		this.state = {
			rowData: [],
			confirmationMessage: false,
			isLoading: false,
			loadingMessage: 'Loading report...',
			nextPageRequest: null,
			pageSize: 500,
			sortBy: 'created_date_asc',
		};
	}

	getAutoRefundOptOut = async data => {
		const { customerId } = Session.getAccountInfo();
		const response = await AccountCommunication.getAutoRefundOptOut(
			customerId,
			data
		);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				nextPageRequest: response.data.next_page_request,
				rowData: response.data.prints,
			});
		} else {
			logToServer({
				category: 'error_autorefundoptout',
				message: response,
			});
		}

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});
	};

	onGridReady = ({ api }) => {
		const { pageSize, sortBy } = this.state;
		this.setState(
			{
				isLoading: true,
				loadingMessage: 'Loading report...',
			},
			() => {
				this.getAutoRefundOptOut({
					page_size: pageSize,
					previous_sort_values: null,
					sort: sortBy,
				});
			}
		);
	};

	// event handler for update button posts to autoRefundOptOut endpoint with updated print status
	onUpdateButtonClick = async () => {
		const { rowData } = this.state;
		const { customerId } = Session.getAccountInfo();
		const autoRefundOptOuts = [];

		rowData.forEach(row => {
			const optOut = {
				universal_id: row.universal_id,
				date_created: row.date_created,
				is_auto_refund_opt_out: row.is_auto_refund_opt_out,
			};
			autoRefundOptOuts.push(optOut);
		});

		const request = {
			cust_id: customerId,
			auto_refund_opt_outs: autoRefundOptOuts,
		};

		const response = await AccountCommunication.autoRefundOptOut(request);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				confirmationMessage: true,
			});
		} else {
			logToServer({
				category: 'error_autorefundoptout',
				message: response,
			});
		}
	};

	// update column state when user sorts the column
	onSortChanged = sortModel => {
		const { pageSize } = this.state;
		const sortIds = {
			is_auto_refund_opt_out: 'auto_refund_opt_out',
			date_created: 'created_date',
			destination_address1: 'to_address_line1',
			confirmation_number: 'tracking_number',
		};
		const sortId = sortIds[sortModel[0].colId];
		const sortBy = `${sortId}_${sortModel[0].sort}`;
		this.setState(
			{
				nextPageRequest: null,
				sortBy,
			},
			() => {
				this.getAutoRefundOptOut({
					page_size: pageSize,
					previous_sort_values: null,
					sort: sortBy,
				});
			}
		);
	};

	render() {
		const {
			confirmationMessage,
			isLoading,
			loadingMessage,
			nextPageRequest,
			pageSize,
			rowData,
		} = this.state;
		const columnDefs = [
			{
				headerName: 'Label Status',
				field: 'is_auto_refund_opt_out',
				flex: 2,
				suppressSizeToFit: false,
				sortable: true,
				cellRenderer: 'LabelStatusCellRenderer',
				cellRendererParams: {
					onSelect: e => {
						const { rowData: currentRowData } = this.state;
						const rowIndex = currentRowData.findIndex(
							r => r.universal_id === e.target.name
						);
						if (rowIndex !== -1) {
							currentRowData[rowIndex].is_auto_refund_opt_out =
								e.target.value === 'shipped';
						}
						this.setState({
							rowData: currentRowData,
							confirmationMessage: false,
						});
					},
				},
			},
			{
				headerName: 'Print Date',
				field: 'date_created',
				flex: 1,
				sort: 'asc',
				suppressSizeToFit: false,
				cellRenderer: params => {
					const date = new Date(params.value);
					return `${formatDate(date)}`;
				},
			},
			{
				headerName: 'Recipient',
				field: 'destination_address1',
				flex: 1,
				suppressSizeToFit: false,
			},
			{
				headerName: 'Tracking #',
				field: 'confirmation_number',
				flex: 2,
				suppressSizeToFit: false,
			},
		];

		return (
			<div className="account-refundeligibilityreport">
				<h1>Refund Assist Report</h1>
				<p>
					The following shipping labels are eligible for refund. Labels marked
					as "Not Shipped" will be submitted for refund as part of the Refund
					Assist program. Please take a moment to review this report for
					accuracy and make any appropriate changes.
				</p>
				<Section>
					<SectionBody>
						<GridPanel
							columnDefs={columnDefs}
							dataqa="refundeligibilityreport-grid"
							fetchData={() => {
								this.getAutoRefundOptOut(nextPageRequest);
							}}
							frameworkComponents={{ LabelStatusCellRenderer }}
							infiniteScroll
							nextPage={nextPageRequest !== null}
							nextPageRequest={nextPageRequest}
							noRowsOverlayParams={{
								noDataOverlayText: true,
							}}
							onGridReady={this.onGridReady}
							onSortChanged={this.onSortChanged}
							pageSize={pageSize}
							rowData={rowData}
							showEndOfDataMessage={false}
							showPagination={false}
						/>
					</SectionBody>
				</Section>
				{isLoading && <Spinner msg={loadingMessage} />}
				<div className="bottom-section">
					<p>
						PLEASE NOTE: You must destroy any unused labels as required by
						Domestic Mail Manual Section 604.4 and the{' '}
						<a target="_blank" href="terms/General">
							Stamps.com terms and conditions
						</a>
						.
					</p>
					<WrappedButton
						className="update-button"
						label="Update Label Status"
						disabled={!rowData || rowData.length === 0}
						onClick={this.onUpdateButtonClick}
						dataqa="refund-eligibility-report-update"
					/>
					{confirmationMessage && (
						<div className="confirmation-message">
							Thank you! Your label status has been updated.
						</div>
					)}
				</div>
			</div>
		);
	}
}

export default RefundEligibilityReport;
